import { useMemo } from 'react';

import { RollsPlayer } from '@/entities/rolls';

const MIN_VISUAL_ANGLE = 12;

export interface WheelSegment extends RollsPlayer {
  angle: number;
  startAngle: number;
  cumDashLen: number;
  percentage: number;
}

export function useWheelSegments(players: RollsPlayer[], circumference: number): WheelSegment[] {
  return useMemo(() => {
    if (players.length === 0) return [];
    const totalBet = players.reduce((sum, p) => sum + p.betAmount, 0);
    if (totalBet === 0) return [];

    let rawAngles = players.map((p) => (p.betAmount / totalBet) * 360);

    // Enforce minimum visible angle
    const tooSmall = rawAngles.filter((a) => a > 0 && a < MIN_VISUAL_ANGLE);
    if (tooSmall.length > 0) {
      const deficit = tooSmall.reduce((s, a) => s + (MIN_VISUAL_ANGLE - a), 0);
      const largeTotal = rawAngles.filter((a) => a >= MIN_VISUAL_ANGLE).reduce((s, a) => s + a, 0);
      rawAngles = rawAngles.map((a) => {
        if (a > 0 && a < MIN_VISUAL_ANGLE) return MIN_VISUAL_ANGLE;
        if (a >= MIN_VISUAL_ANGLE) return a - (a / largeTotal) * deficit;
        return a;
      });
    }

    let cumAngle = 0;
    return players.map((p, i) => {
      const angle = rawAngles[i];
      const startAngle = cumAngle;
      cumAngle += angle;
      const cumDashLen = (cumAngle / 360) * circumference;
      const percentage = Math.round((p.betAmount / totalBet) * 100);
      return { ...p, angle, startAngle, cumDashLen, percentage };
    });
  }, [players, circumference]);
}
